/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Group, Link} from '@ion/core/src/types/entities';
import {DropDownDefaultLevelProps, DropDownInfiniteScrollProps} from '@ion/dls/src/components/menus/Dropdown';
import {AdminGroupEditor} from '@ion/dls/src/editors/AdminGroupEditor';
import {getMemberIds} from '@ion/dls/src/editors/GroupEditorUtils';
import {viewDeviceById} from '../../devices/state';
import {viewAdminAsset} from '../assets/state';
import {useAuthenticatedUser} from '../users/useAuthenticatedUser';
import {updateGroupParentAccount} from './helpers/GroupUtils';
import {editGroup, editGroupConfirm} from './state';
import {groupSelector} from './state-selectors';

// defines the prop definitiions for the EditGroupEditor
export interface EditGroupEditorProps extends DropDownDefaultLevelProps,
  DropDownInfiniteScrollProps {
  isCtcAdmin: boolean;
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  hasInheritableGroups?: boolean;
};

const getMembersToDelete = (actualMembers, group: Group) => {
  let deleteDeviceMembers = [];
  let deleteAssetMembers = [];
  if (group.devices?.ids?.length || group.assets?.ids?.length) {
    const {devices, assets} = group;
    deleteDeviceMembers = filterMembers(devices, actualMembers?.devices);
    deleteAssetMembers  = filterMembers(assets, actualMembers?.assets);
    return {
      devices         : deleteDeviceMembers,
      assets          : deleteAssetMembers,
      groupId         : group?.id,
      allAssetsClear  : false,
      allDevicesClear : deleteDeviceMembers.length > 1024 ||
                        deleteDeviceMembers.length === actualMembers?.devices?.length
    };
  } else {
    return {
      devices         : getMemberIds(actualMembers?.devices),
      assets          : getMemberIds(actualMembers?.assets),
      groupId         : group?.id,
      allAssetsClear  : false,
      allDevicesClear : actualMembers?.devices?.length > 1024
    };
  }
};

const filterMembers = (members, actualMembers) => {
  let deleteMembers     = [];
  const selectedMembers = [...members?.ids];
  const actualMemberIds = getMemberIds(actualMembers);
  if (actualMemberIds?.length) {
    deleteMembers = actualMemberIds.filter(member => !selectedMembers.includes(member));
  }

  return deleteMembers;
};

/**
 * It will show EditGroupEditor dialog when use click on Edit button from grid
 */
export const EditGroupEditor = ({
  isCtcAdmin = false,
  isChipClickable = false,
  includeDeviceGridCheckboxes = false,
  hasInheritableGroups = false,
  includeAssetGridCheckboxes = false,
  isInfiniteScroll = false
}: EditGroupEditorProps) => {
  const dispatch      = useDispatch();
  const {account}     = useAuthenticatedUser();
  const selectedGroup = useSelector(groupSelector);
  const {globalGroup} = useSelector(({globalGroup}) => globalGroup);
  const {
    selected: group,
    loading
  } = useSelector(({groups: {edit}}) => edit);

  const [actualMembers, setActualMembers] = useState<Group>([]);

  const onClose = function() {
    dispatch(editGroup(null, false));
  };

  const onSave = function(group: Group) {
    const deleteMembers  = getMembersToDelete(actualMembers, group);
    const sanitizedGroup = updateGroupParentAccount(group, selectedGroup?.account);
    dispatch(editGroupConfirm(sanitizedGroup, deleteMembers));
  };

  const getActualMembers = useCallback((groupMembers) => {
    setActualMembers(groupMembers);
  }, [setActualMembers]);

  const onCancel = function() {
    dispatch(editGroup(null, false));
  };

  const onViewDevice = useCallback((deviceId) => {
    dispatch(viewDeviceById(deviceId));
  }, []);

  const onViewAsset = useCallback((assetId) => {
    dispatch(viewAdminAsset(assetId, true, true));
  }, []);

  return (
    <AdminGroupEditor open
      group={group}
      isChipClickable={isChipClickable}
      onViewDevice={onViewDevice}
      onViewAsset={onViewAsset}
      isCtcAdmin={isCtcAdmin}
      account={globalGroup || account}
      hasInheritableGroups={hasInheritableGroups}
      getActualMembers={getActualMembers}
      loading={loading}
      includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
      includeAssetGridCheckboxes={includeAssetGridCheckboxes}
      onSave={onSave}
      onCancel={onCancel}
      onCloseDialog={onClose}
      isInfiniteScroll={isInfiniteScroll} />
  );
};

export default EditGroupEditor;
